import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { MapPin, Zap, Settings, Cpu, Wrench, Search, ChevronRight, Activity, X, Filter, RotateCcw } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { ElectricalLocationCard } from './ElectricalLocationCard';

const MAINT_THRESHOLD_DAYS = 90;

const EMPTY_FILTERS = {
  search: '',
  utilityProvider: '',
  serviceVoltage: '',
  phase: '',
  serviceCapacity: '',
  mainSwitchboard: '',
  serviceDisconnectRating: '',
  loadMin: '',
  loadMax: '',
  location: '',
};

export const ScheduleSection = ({ projectId, csiItem, onItemCount }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationDefaultTab, setLocationDefaultTab] = useState('panels');

  // Exclusive dropdown behavior (same as MaintenanceSection)
  const [openDropdown, setOpenDropdown] = useState(null);
  const makeDropdownProps = (key) => ({
    open: openDropdown === key,
    onOpenChange: (open) => setOpenDropdown(prev => open ? key : (prev === key ? null : prev)),
    modal: false,
  });

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-systems/${projectId}/schedule-overview`);
      setData(res.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (onItemCount && data) onItemCount(data.totalLocations || 0); }, [data, onItemCount]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const applyFilters = () => setAppliedFilters({ ...filters });
  const clearAll = () => { setFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS); };

  const hasDraftChanges = useMemo(() => JSON.stringify(filters) !== JSON.stringify(appliedFilters), [filters, appliedFilters]);
  const isFiltered = useMemo(() => Object.values(appliedFilters).some(v => v !== ''), [appliedFilters]);

  // General Info filters (project-level AND logic) — uses appliedFilters
  const giPassesFilters = useMemo(() => {
    const gi = data?.generalInfo;
    if (!gi) return true;

    const textMatch = (filterVal, dataVal) => {
      if (!filterVal.trim()) return true;
      return (dataVal || '').toLowerCase().includes(filterVal.trim().toLowerCase());
    };

    if (!textMatch(appliedFilters.utilityProvider, gi.utilityProvider)) return false;
    if (!textMatch(appliedFilters.serviceVoltage, gi.serviceVoltage)) return false;
    if (!textMatch(appliedFilters.serviceCapacity, gi.serviceCapacity)) return false;
    if (!textMatch(appliedFilters.mainSwitchboard, gi.mainSwitchboard)) return false;
    if (!textMatch(appliedFilters.serviceDisconnectRating, gi.serviceDisconnectRating)) return false;

    if (appliedFilters.phase && gi.phase && appliedFilters.phase !== gi.phase) return false;

    const load = gi.estimatedLoad != null ? Number(gi.estimatedLoad) : null;
    if (appliedFilters.loadMin !== '' && load != null && load < Number(appliedFilters.loadMin)) return false;
    if (appliedFilters.loadMax !== '' && load != null && load > Number(appliedFilters.loadMax)) return false;

    return true;
  }, [data, appliedFilters]);

  // Location-level filters — uses appliedFilters
  const filteredLocations = useMemo(() => {
    if (!data?.locations || !giPassesFilters) return [];
    let locs = data.locations;
    if (appliedFilters.location) {
      locs = locs.filter(loc => loc.location === appliedFilters.location);
    }
    const q = appliedFilters.search.trim().toLowerCase();
    if (!q) return locs;
    return locs.filter(loc =>
      loc.location.toLowerCase().includes(q) ||
      loc.panelTypes.some(t => t.toLowerCase().includes(q)) ||
      loc.deviceCategories.some(c => c.toLowerCase().includes(q)) ||
      (loc.deviceTypes || []).some(t => t.toLowerCase().includes(q))
    );
  }, [data, appliedFilters.search, appliedFilters.location, giPassesFilters]);

  const handleLocationClick = (locationName) => { setLocationDefaultTab('panels'); setSelectedLocation(locationName); };
  const handleDeviceCountClick = (e, locationName) => { e.stopPropagation(); setLocationDefaultTab('devices'); setSelectedLocation(locationName); };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
        <Activity className="h-8 w-8 mx-auto text-slate-300 animate-pulse mb-3" />
        <p className="text-sm text-slate-500">Loading dashboard...</p>
      </div>
    );
  }

  if (!data || data.totalLocations === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="schedule-empty-state">
        <MapPin className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No Locations Found</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          Locations will appear here automatically when you add panels, equipment, or devices with location information in their respective tabs.
        </p>
      </div>
    );
  }

  const gi = data.generalInfo;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4" data-testid="schedule-section">

        {/* Filter Panel */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3" data-testid="dashboard-filter-panel">
          {/* Row 1: Project-level filters */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <FilterInput label="Utility Provider" value={filters.utilityProvider} onChange={v => setFilter('utilityProvider', v)} placeholder="Search provider..." testId="filter-utilityProvider" />
            <FilterInput label="Service Voltage" value={filters.serviceVoltage} onChange={v => setFilter('serviceVoltage', v)} placeholder="Search voltage..." testId="filter-serviceVoltage" />
            <FilterInput label="Service Capacity" value={filters.serviceCapacity} onChange={v => setFilter('serviceCapacity', v)} placeholder="Search capacity..." testId="filter-serviceCapacity" />
            <FilterInput label="Main Switchboard" value={filters.mainSwitchboard} onChange={v => setFilter('mainSwitchboard', v)} placeholder="Search switchboard..." testId="filter-mainSwitchboard" />
            <FilterInput label="Disconnect Rating" value={filters.serviceDisconnectRating} onChange={v => setFilter('serviceDisconnectRating', v)} placeholder="Search rating..." testId="filter-disconnectRating" />
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Phase</label>
              <Select value={filters.phase || 'all'} onValueChange={v => setFilter('phase', v === 'all' ? '' : v)} {...makeDropdownProps('phase')}>
                <SelectTrigger className="h-8 text-sm" data-testid="filter-phase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">1 Phase</SelectItem>
                  <SelectItem value="3">3 Phase</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Load range + location + text search + actions */}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex items-end gap-1.5">
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Load Min (kW)</label>
                <Input type="number" min="0" step="any" value={filters.loadMin} onChange={e => setFilter('loadMin', e.target.value)} placeholder="Min kW" className="h-8 text-sm w-24" onKeyDown={e => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }} data-testid="filter-loadMin" />
              </div>
              <span className="text-slate-300 pb-1.5">–</span>
              <div>
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Load Max (kW)</label>
                <Input type="number" min="0" step="any" value={filters.loadMax} onChange={e => setFilter('loadMax', e.target.value)} placeholder="Max kW" className="h-8 text-sm w-24" onKeyDown={e => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }} data-testid="filter-loadMax" />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Location</label>
              <Select value={filters.location || 'all'} onValueChange={v => setFilter('location', v === 'all' ? '' : v)} {...makeDropdownProps('location')}>
                <SelectTrigger className="h-8 text-sm w-44" data-testid="filter-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {(data?.locations || []).map(loc => (
                    <SelectItem key={loc.location} value={loc.location}>{loc.location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={filters.search}
                onChange={e => setFilter('search', e.target.value)}
                placeholder="Location, panel, device..."
                className="pl-8 pr-8 h-8 text-sm"
                data-testid="schedule-search-input"
              />
              {filters.search && (
                <button onClick={() => setFilter('search', '')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="schedule-search-clear">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" onClick={applyFilters} disabled={!hasDraftChanges} className="h-8 text-xs gap-1" data-testid="filter-apply-btn">
                <Filter className="h-3 w-3" />Filter
              </Button>
              {isFiltered && (
                <Button variant="outline" size="sm" onClick={clearAll} className="h-8 text-xs gap-1" data-testid="filter-clear-all">
                  <RotateCcw className="h-3 w-3" />Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard icon={MapPin} label="Locations" value={data.totalLocations} color="blue" />
          <SummaryCard icon={Zap} label="Panels" value={data.totalPanels} color="amber" />
          <SummaryCard icon={Settings} label="Equipment" value={data.totalEquipment} color="emerald" />
          <SummaryCard icon={Cpu} label="Devices" value={data.totalDevices} color="violet" />
        </div>

        {/* Location List */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="schedule-location-list">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
            <div className="col-span-3">Location</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-1 text-center">Panels</div>
            <div className="col-span-1 text-center">Equip.</div>
            <div className="col-span-1 text-center">Devices</div>
            <div className="col-span-3">Device Types</div>
            <div className="col-span-2 text-right pr-2">Total</div>
          </div>

          {filteredLocations.map((loc) => (
            <div
              key={loc.location}
              onClick={() => handleLocationClick(loc.location)}
              className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-blue-50/50 transition-colors cursor-pointer group"
              data-testid={`schedule-location-row-${loc.location}`}
            >
              <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-800 truncate">{loc.location}</div>
                  {loc.totalWattage > 0 && (
                    <div className="text-xs text-slate-400 mt-0.5">{loc.totalWattage.toLocaleString()} W</div>
                  )}
                </div>
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <LocationStatusBadge lastMaintenanceDate={data.lastMaintenanceDate} />
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <CountBadge count={loc.panelCount} color="amber" icon={Zap} />
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <CountBadge count={loc.equipmentCount} color="emerald" icon={Settings} />
              </div>
              <div className="col-span-1 flex items-center justify-center">
                {loc.deviceCount > 0 ? (
                  <button
                    onClick={(e) => handleDeviceCountClick(e, loc.location)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 hover:ring-1 hover:ring-violet-300 transition-all cursor-pointer"
                    data-testid={`schedule-device-count-btn-${loc.location}`}
                  >
                    <Cpu className="h-3 w-3" />
                    {loc.deviceCount}
                  </button>
                ) : (
                  <span className="text-xs text-slate-300">-</span>
                )}
              </div>
              <div className="col-span-3 flex items-center min-w-0">
                <DeviceTypesDisplay types={loc.deviceTypes || []} />
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1.5">
                <span className="text-sm font-semibold text-slate-700">{loc.totalAssets}</span>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
              </div>
            </div>
          ))}

          {filteredLocations.length === 0 && (
            <div className="py-12 text-center" data-testid="schedule-no-results">
              <Search className="h-8 w-8 mx-auto text-slate-200 mb-2" />
              {!giPassesFilters ? (
                <>
                  <p className="text-sm text-slate-500">No results match the current filters</p>
                  <p className="text-xs text-slate-400 mt-1">Project data does not match the applied criteria</p>
                </>
              ) : (
                <p className="text-sm text-slate-500">No locations match &quot;{filters.search.trim()}&quot;</p>
              )}
              <button onClick={clearAll} className="text-xs text-orange-600 hover:underline mt-2" data-testid="schedule-clear-filter">
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Recent Maintenance */}
        {data.recentMaintenance?.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="schedule-recent-maintenance">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="h-4 w-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-700">Recent Maintenance</h4>
              <span className="text-xs text-slate-400">({data.maintenanceCount} total)</span>
            </div>
            <div className="space-y-2">
              {data.recentMaintenance.map((m, i) => (
                <div key={m.id || i} className="flex items-center gap-3 text-xs text-slate-600 py-1.5 border-b border-slate-50 last:border-b-0">
                  <span className="text-slate-400 w-20 flex-shrink-0">{m.date}</span>
                  <span className="font-medium truncate">{m.action}</span>
                  <span className="text-slate-400 ml-auto flex-shrink-0">{m.technician}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <ElectricalLocationCard
          open={!!selectedLocation}
          onOpenChange={(open) => { if (!open) setSelectedLocation(null); }}
          projectId={projectId}
          location={selectedLocation}
          defaultTab={locationDefaultTab}
        />
      </div>
    </TooltipProvider>
  );
};

/* ── Filter Input ── */
const FilterInput = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">{label}</label>
    <div className="relative">
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-sm pr-7"
        data-testid={testId}
      />
      {value && (
        <button onClick={() => onChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  </div>
);

/* ── Helper Components ── */

const SummaryCard = ({ icon: Icon, label, value, color }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    violet: 'bg-violet-50 text-violet-600',
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-center gap-3" data-testid={`schedule-summary-${label.toLowerCase()}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div>
        <div className="text-lg font-bold text-slate-800">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
};

const CountBadge = ({ count, color, icon: Icon }) => {
  if (count === 0) return <span className="text-xs text-slate-300">-</span>;
  const colorMap = { amber: 'bg-amber-50 text-amber-700', emerald: 'bg-emerald-50 text-emerald-700' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[color]}`}>
      <Icon className="h-3 w-3" />{count}
    </span>
  );
};

const LocationStatusBadge = ({ lastMaintenanceDate }) => {
  let label, colorClass, tooltipText;
  if (!lastMaintenanceDate) {
    label = 'No Maintenance'; colorClass = 'bg-slate-100 text-slate-500 border-slate-200'; tooltipText = 'No maintenance record';
  } else {
    const daysDiff = Math.floor((new Date() - new Date(lastMaintenanceDate)) / 86400000);
    if (daysDiff > MAINT_THRESHOLD_DAYS) {
      label = 'Warning'; colorClass = 'bg-amber-50 text-amber-700 border-amber-200'; tooltipText = `Last maintenance: ${lastMaintenanceDate} (${daysDiff} days ago)`;
    } else {
      label = 'OK'; colorClass = 'bg-green-50 text-green-700 border-green-200'; tooltipText = `Last maintenance: ${lastMaintenanceDate}`;
    }
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${colorClass} cursor-default`} data-testid="schedule-status-badge">{label}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">{tooltipText}</TooltipContent>
    </Tooltip>
  );
};

const DeviceTypesDisplay = ({ types }) => {
  if (!types || types.length === 0) return <span className="text-xs text-slate-300">-</span>;
  const MAX_VISIBLE = 2;
  const visible = types.slice(0, MAX_VISIBLE);
  const remaining = types.length - MAX_VISIBLE;
  if (types.length <= MAX_VISIBLE) return <span className="text-xs text-slate-600 truncate">{types.join(', ')}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-xs text-slate-600 truncate cursor-default" data-testid="schedule-device-types">
          {visible.join(', ')} <span className="text-slate-400 font-medium">+{remaining}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-xs">
        <div className="space-y-0.5 text-xs">{types.map((t, i) => <div key={i}>{t}</div>)}</div>
      </TooltipContent>
    </Tooltip>
  );
};
